import React, { forwardRef, useCallback, useRef, useState } from 'react';

import type { InputBaseProps } from '@mui/material';
import { IconButton, InputBase } from '@mui/material';
import clsx from 'clsx';
import { SquarePen } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { AlineTooltip } from './AlineTooltip';
import { AutosizeInput } from './AutosizeInput';

const AutosizeInputAdapter = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & { ownerState?: unknown }
>(function AutosizeInputAdapter({ className, ownerState: _ownerState, ...props }, ref) {
  return <AutosizeInput ref={ref} inputClassName={className} {...props} />;
});

/**
 * Auto-sizing inline input. Uses the local AutosizeInput component to handle
 * width measurement and passes through the full InputBaseProps API.
 */
export const InlineEditInput = forwardRef(function InlineEditInput(
  props: InputBaseProps,
  ref: React.Ref<HTMLInputElement>
) {
  return <InputBase ref={ref} inputComponent={AutosizeInputAdapter} {...props} />;
});

const useEditFieldStyles = makeStyles<void, 'iconButton'>()((theme, _params, classes) => ({
  clickable: {
    cursor: 'pointer',
  },
  /** Inline SVG sits on the baseline and adds descender space to the button. */
  iconButton: {
    '& svg': {
      display: 'block',
    },
    '&:focus-visible': {
      opacity: 1,
    },
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 120ms ease',
  },
  /** Holds the pencil's footprint while editing so the row keeps its width. */
  iconSpacer: {
    flexShrink: 0,
  },
  /**
   * Caller typography lives on the wrapping span (same class as display
   * mode). Beat MUI InputBase's own font-size / line-height / padding so the
   * edit box is the same height as the text it replaces.
   */
  input: {
    '& .MuiInputBase-input': {
      appearance: 'none',
      border: 0,
      boxSizing: 'border-box',
      font: 'inherit',
      height: 'auto',
      lineHeight: 'inherit',
      margin: 0,
      padding: 0,
    },
    '&.MuiInputBase-root': {
      color: 'inherit',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      lineHeight: 'inherit',
      padding: 0,
    },
    maxWidth: '100%',
    minWidth: 0,
  },
  row: {
    [`&:hover .${classes.iconButton}`]: {
      opacity: 1,
    },
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(0.5),
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  /**
   * Flex, not inline: an inline span's line-box strut is taller than the
   * input it wraps and would still shift the row when editing starts.
   */
  typographyWrap: {
    display: 'flex',
    maxWidth: '100%',
    minWidth: 0,
  },
}));

/** MUI's padding on a small IconButton, which sets the pencil's footprint. */
const ICON_BUTTON_PADDING_PX = 5;

interface InlineEditFieldProps {
  onSave: (value: string) => void;
  value: string;
  classes?: { display?: string; input?: string; placeholder?: string };
  disabled?: boolean;
  /**
   * When false, only the pencil starts editing so a parent row click still
   * fires. Default true: the display text is click-to-edit.
   */
  editOnDisplayClick?: boolean;
  editTooltip?: string;
  iconSize?: number;
  initiallyEditing?: boolean;
  maxLength?: number;
  /** Publish draft edits immediately; Escape restores the value from the start of editing. */
  onChange?: (value: string) => void;
  placeholder?: string;
  textPlaceholder?: string;
}

/**
 * Click-to-edit field with a SquarePen icon. The row fills its parent so
 * hovering anywhere on it reveals the pencil. Clicking the row (or the
 * pencil) switches to an auto-sized inline input. Saves on blur or Enter,
 * cancels on Escape.
 */
export function InlineEditField({
  classes: classesProp,
  disabled,
  editOnDisplayClick = true,
  editTooltip,
  iconSize = 14,
  initiallyEditing = false,
  maxLength,
  onChange,
  onSave,
  placeholder = 'Untitled',
  textPlaceholder = 'Untitled',
  value,
}: InlineEditFieldProps) {
  const draftRef = useRef(value);
  // Set once the edit ends, so the blur from unmounting the input does not save again.
  const closedRef = useRef(false);
  const { classes } = useEditFieldStyles();
  const originalValueRef = useRef(value);
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(initiallyEditing);

  const commit = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    setEditing(false);
    const trimmed = draftRef.current.trim();
    // Live edits already update value; still finalize them through onSave.
    if (onChange || trimmed !== value) onSave(trimmed);
  }, [onChange, onSave, value]);

  const cancel = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    const original = originalValueRef.current;
    draftRef.current = original;
    setDraft(original);
    onChange?.(original);
    setEditing(false);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') commit();
      if (e.key === 'Escape') cancel();
    },
    [cancel, commit]
  );

  const startEditing = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closedRef.current = false;
      originalValueRef.current = value;
      draftRef.current = value;
      setDraft(value);
      setEditing(true);
    },
    [value]
  );

  const changeDraft = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      draftRef.current = event.target.value;
      setDraft(event.target.value);
      onChange?.(event.target.value);
    },
    [onChange]
  );

  const iconFootprint = iconSize + ICON_BUTTON_PADDING_PX * 2;

  if (editing) {
    return (
      <div className={classes.row}>
        <span className={clsx(classes.typographyWrap, classesProp?.display)}>
          <InlineEditInput
            autoFocus={!disabled}
            className={clsx(classes.input, classesProp?.input)}
            disabled={disabled}
            inputProps={{ maxLength }}
            placeholder={placeholder}
            sx={{ maxWidth: '100%', minWidth: 0 }}
            value={draft}
            onBlur={commit}
            onChange={changeDraft}
            onClick={e => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          />
        </span>
        {!disabled && (
          <div
            className={classes.iconSpacer}
            style={{ height: iconFootprint, width: iconFootprint }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(classes.row, !disabled && editOnDisplayClick && classes.clickable)}
      style={disabled ? { opacity: 0.5 } : undefined}
      onClick={disabled || !editOnDisplayClick ? undefined : startEditing}
    >
      <span
        className={clsx(
          classes.typographyWrap,
          classesProp?.display,
          !value && classesProp?.placeholder
        )}
      >
        {value || textPlaceholder}
      </span>
      {!disabled && (
        <InlineEditIconButton
          editTooltip={editTooltip}
          iconSize={iconSize}
          onClick={startEditing}
        />
      )}
    </div>
  );
}

function InlineEditIconButton({
  editTooltip,
  iconSize,
  onClick,
}: {
  iconSize: number;
  onClick: (event: React.MouseEvent) => void;
  editTooltip?: string;
}) {
  const { classes } = useEditFieldStyles();
  const button = (
    <IconButton className={classes.iconButton} size="small" onClick={onClick}>
      <SquarePen size={iconSize} />
    </IconButton>
  );

  if (!editTooltip) return button;

  return (
    <AlineTooltip arrow placement="top" title={editTooltip}>
      {button}
    </AlineTooltip>
  );
}
